import { useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode, type Ref } from 'react'
import { api, ApiError } from '../lib/api'
import { acceptsNote, mayChange, noteHead, notesKey, ordered } from '../lib/note'
import { explain } from '../lib/staffErrors'
import { formatMoment } from '../lib/staffText'
import { NOTE_LIMIT, type NoteEntry, type TicketState } from '../lib/types'
import { useStaffText } from './StaffText'
import '../styles/counter.css'

/** What the history needs of a visit. */
type NoteTicket = { id: string; state: TicketState; note?: string; noteCount?: number }

/** Lets a dialog add the unsent note before an action that moves the visit away. */
export type NoteHistoryHandle = { save: () => Promise<boolean> }

/**
 * A visit's notes, oldest first ("Desk 1 · Leyla · 10:42", then the text), and a
 * box to add one. The server decides who may edit or delete an entry (its author,
 * while the customer is still at their desk); `edits` lets a screen offer only adding.
 */
export function NoteHistory({
  ticket,
  zone,
  list = true,
  adds = true,
  edits = true,
  label,
  placeholder,
  handle,
}: {
  ticket: NoteTicket
  zone?: string
  /** Show the entries; off in a dialog that only adds. */
  list?: boolean
  adds?: boolean
  edits?: boolean
  label?: ReactNode
  placeholder?: string
  handle?: Ref<NoteHistoryHandle>
}) {
  const { lang, say } = useStaffText()
  const [notes, setNotes] = useState<NoteEntry[]>([])
  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const draftRef = useRef('')
  const ticketId = ticket.id
  const key = notesKey(ticket)

  useEffect(() => {
    if (!list) return
    let live = true
    api.ticketNotes(ticketId).then(
      (r) => live && setNotes(ordered(r.notes)),
      () => {},
    )
    return () => {
      live = false
    }
  }, [list, ticketId, key])

  useEffect(() => {
    setDraft('')
    draftRef.current = ''
    setEditing(null)
    setError('')
  }, [ticketId])

  const refused = useCallback(
    (err: unknown) => setError(err instanceof ApiError && err.code === 'VISIT_OVER' ? say('floor.noteGone') : explain(err, lang, 'floor.noteNotSaved')),
    [lang, say],
  )

  const add = useCallback(async (): Promise<boolean> => {
    const text = draftRef.current.trim()
    if (!text) return true
    setBusy(true)
    try {
      const { note } = await api.addTicketNote(ticketId, text)
      draftRef.current = ''
      setDraft('')
      setError('')
      setNotes((now) => ordered([...now.filter((n) => n.id !== note.id), note]))
      return true
    } catch (err) {
      refused(err)
      return false
    } finally {
      setBusy(false)
    }
  }, [ticketId, refused])
  useImperativeHandle(handle, () => ({ save: add }), [add])

  async function saveEdit() {
    if (!editing) return
    setBusy(true)
    try {
      const { note } = await api.editTicketNote(ticketId, editing.id, editing.text)
      setNotes((now) => now.map((n) => (n.id === note.id ? note : n)))
      setEditing(null)
      setError('')
    } catch (err) {
      refused(err)
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: string) {
    setBusy(true)
    try {
      await api.deleteTicketNote(ticketId, id)
      setNotes((now) => now.filter((n) => n.id !== id))
      setError('')
    } catch (err) {
      refused(err)
    } finally {
      setBusy(false)
    }
  }

  const open = acceptsNote(ticket)
  const left = NOTE_LIMIT - draft.length
  return (
    <div className="field counter-note note-history">
      <span>{label ?? say('floor.notes')}</span>
      {list &&
        (notes.length > 0 ? (
          <ol className="note-list">
            {notes.map((n) => (
              <li key={n.id} className="note-entry">
                <small className="note-entry__head">
                  {noteHead(n, say('floor.notes.reception'), formatMoment(n.at, lang, { time: true, zone }))}
                  {n.editedAt && ` · ${say('floor.notes.edited')}`}
                </small>
                {editing?.id === n.id ? (
                  <div className="note-add">
                    <input
                      value={editing.text}
                      maxLength={NOTE_LIMIT}
                      aria-label={say('floor.notes.edit')}
                      autoFocus
                      onChange={(e) => setEditing({ id: n.id, text: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          void saveEdit()
                        } else if (e.key === 'Escape') setEditing(null)
                      }}
                    />
                    <button type="button" className="btn btn--sm btn--secondary" disabled={busy || !editing.text.trim()} onClick={() => void saveEdit()}>
                      {say('common.save')}
                    </button>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setEditing(null)}>
                      {say('common.cancel')}
                    </button>
                  </div>
                ) : (
                  <p className="note-entry__text">{n.text}</p>
                )}
                {mayChange(n, ticket, edits) && editing?.id !== n.id && (
                  <span className="note-entry__do">
                    <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => setEditing({ id: n.id, text: n.text })}>
                      {say('floor.notes.edit')}
                    </button>
                    <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => void remove(n.id)}>
                      {say('common.delete')}
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ol>
        ) : (
          !adds && <small className="note-entry__head">{say('floor.notes.none')}</small>
        ))}
      {adds && open && (
        <div className="note-add">
          <input
            id={`note-${ticketId}`}
            value={draft}
            maxLength={NOTE_LIMIT}
            placeholder={placeholder ?? say('floor.notes.placeholder')}
            aria-label={placeholder ?? say('floor.notes.placeholder')}
            onChange={(e) => {
              draftRef.current = e.target.value
              setDraft(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void add()
              }
            }}
          />
          <button type="button" className="btn btn--sm btn--secondary" disabled={busy || !draft.trim()} onClick={() => void add()}>
            {say('common.add')}
          </button>
        </div>
      )}
      {(error || (adds && left <= 40)) && (
        <small data-bad={error ? '' : undefined} role={error ? 'alert' : undefined}>
          {error || say('floor.charactersLeft', { n: left })}
        </small>
      )}
    </div>
  )
}
